// =====================================================
// 📁 POSDateField.tsx
// =====================================================

import React, { useEffect, useRef } from 'react';

import flatpickr from 'flatpickr';

import 'flatpickr/dist/flatpickr.min.css';

import { Spanish } from 'flatpickr/dist/l10n/es.js';

import { IconKey } from '../../../types/system/icons';

import InputIconByKey from '../../Forms/Input/InputIconByKey';

interface POSDateFieldProps {
  label: string;

  name: string;

  value: string;

  onChange?: (date: string) => void;

  disabled?: boolean;

  placeholder?: string;

  iconKey?: IconKey;

  enableTime?: boolean;

  className?: string;

  error?: string;
}

const POSDateField: React.FC<POSDateFieldProps> = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  placeholder = 'Seleccionar fecha',
  iconKey = 'calendar',
  enableTime = false,
  className = '',
  error,
}) => {
  // =====================================================
  // 🔹 Refs
  // =====================================================
  const datePickerRef = useRef<HTMLInputElement>(null);

  const flatpickrInstance = useRef<flatpickr.Instance | null>(null);

  // =====================================================
  // 🔹 Helpers
  // =====================================================
  const formatLocalDate = (date: Date) => {
    return date.toLocaleDateString('sv-SE');
  };

  const formatLocalDateTime = (date: Date) => {
    return date
      .toLocaleString('sv-SE', {
        hour12: false,
      })
      .replace(' ', 'T')
      .slice(0, 16);
  };

  // =====================================================
  // 🚀 Init Flatpickr
  // =====================================================
  useEffect(() => {
    if (datePickerRef.current && !flatpickrInstance.current) {
      flatpickrInstance.current = flatpickr(datePickerRef.current, {
        locale: Spanish,

        dateFormat: enableTime ? 'Y-m-d H:i' : 'Y-m-d',

        defaultDate: value,

        enableTime,

        allowInput: true,

        disableMobile: true,

        clickOpens: !disabled,

        appendTo: document.body,

        onChange: (selectedDates) => {
          if (selectedDates.length === 0) {
            return;
          }

          const selectedDate = selectedDates[0];

          if (enableTime) {
            onChange?.(formatLocalDateTime(selectedDate));
          } else {
            onChange?.(formatLocalDate(selectedDate));
          }
        },
      });
    }

    return () => {
      flatpickrInstance.current?.destroy();

      flatpickrInstance.current = null;
    };
  }, []);

  // =====================================================
  // 🔹 Sync Value
  // =====================================================
  useEffect(() => {
    if (flatpickrInstance.current && value) {
      flatpickrInstance.current.setDate(value, false);
    }
  }, [value]);

  // =====================================================
  // 🔹 Disabled Sync
  // =====================================================
  useEffect(() => {
    if (flatpickrInstance.current) {
      flatpickrInstance.current.set('clickOpens', !disabled);
    }
  }, [disabled]);

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div
      className={`
      flex
      min-w-0
      flex-col
      gap-1

      ${className}
    `}
    >
      {/* 🔹 Label */}
      <label
        className="
        text-xs
        font-medium
        text-body
        dark:text-bodydark
      "
      >
        {label}
      </label>

      {/* 🔹 Field */}
      <div
        className={`
        flex
        h-[44px]
        min-w-0
        items-center

        overflow-hidden

        rounded-xl
        border

        ${error ? 'border-danger' : 'border-stroke dark:border-strokedark'}

        bg-white
        shadow-1

        transition-all
        duration-200

        focus-within:border-primary

        hover:border-primary
        hover:shadow-2

        dark:bg-boxdark-2

        ${disabled ? 'cursor-not-allowed opacity-60' : ''}
      `}
      >
        {/* 🔹 Icon */}
        <div
          className="
          flex
          h-full
          w-[48px]
          shrink-0

          items-center
          justify-center

          border-r
          border-stroke

          bg-gray

          dark:border-strokedark
          dark:bg-meta-4
        "
        >
          <div className="text-body dark:text-bodydark">
            <InputIconByKey iconKey={iconKey} className="h-5 w-5" />
          </div>
        </div>

        {/* 🔹 Input */}
        <input
          ref={datePickerRef}
          type="text"
          name={name}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          autoComplete="off"
          className="
          h-full
          min-w-0
          flex-1

          truncate

          bg-transparent

          px-4

          text-sm
          font-medium

          text-black
          outline-none

          placeholder:text-bodydark2

          dark:text-bodydark1
        "
        />
      </div>

      {/* 🔹 Error */}
      {error && (
        <p
          className="
          mt-1
          pl-1

          text-xs
          font-medium

          text-danger
        "
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default POSDateField;
